import {combineEpics, Epic, StateObservable} from 'redux-observable';
import {loadBridgeHistory, loadHistoricalData} from '../../../services/ViewerApiService';
import {isActionOf} from 'typesafe-actions';
import {filter, map, switchMap,} from 'rxjs/operators';
import {
    storeNewBridgeHistoricalGraphData,
    storeNewGraphData,
} from '../../../scenes/MapScene/actions/reducers/activeDialogs';
import {RootState} from '../../../reducer';
import {EMPTY} from 'rxjs';
import {openGraphDialog} from '../../../scenes/MapScene/actions/travelTimeDialog';
import {mapSceneOpenBridgeGraphDialog} from '../../../scenes/MapScene/actions/bridgeDialog';
import {viewerApiServiceReceivedHistoricalData} from '../../../services/ViewerApiService/actions/historicalDataData';
import {viewerApiServiceReceivedBridgeHistory} from '../../../services/ViewerApiService/actions/loadBridgeHistory';
import {PlotData} from 'plotly.js';
import {chartColors} from '../../../styles';
import {ActiveDialogVectorLayerFeatureTravelTimeV2GraphData} from '../../../interfaces/ActiveDialog';
import {VectorLayerStateTravelTimeV2} from '../../../interfaces/VectorLayerSources/VectorLayerStateTravelTimeV2';

const loadBridgeDataOnOpenBridgeGraphDialogEffect: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(mapSceneOpenBridgeGraphDialog)),
        switchMap(({payload: {isrsCode}}) => loadBridgeHistory(isrsCode)),
    );

const loadHistoricalDataOnOpenGraphDialogEffect: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(openGraphDialog)),
        switchMap((action) =>
            loadHistoricalData(
                action.payload.dialog,
                action.payload.mapSourceID,
                action.payload.featureID,
                action.payload.locationID,
                action.payload.timestamp,
            ),
        ),
    );

const saveBridgeGraphDataOnReceivedBridgeHistoryEffect: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(viewerApiServiceReceivedBridgeHistory)),
        map(({payload: {isrsCode, bridgeHistoryData}}) =>
            storeNewBridgeHistoricalGraphData(isrsCode, bridgeHistoryData),
        ),
    );

const saveGraphDataOnReceivedHistoricalDataEffect: Epic = (action$, state$: StateObservable<RootState>) =>
    action$.pipe(
        filter(isActionOf(viewerApiServiceReceivedHistoricalData)),
        map((action) => {
            const {data, dataType, featureID, mapSourceID} = action.payload;

            switch (dataType) {
                case 'travel-time-v2':
                    const mapSourceTravelTimeV2 = state$.value.mapScene.data.sources[mapSourceID];

                    if (mapSourceTravelTimeV2.dataLoadMethod !== 'VectorTiles') {
                        return EMPTY;
                    }
                    const travelTimeCurrentDataV2: VectorLayerStateTravelTimeV2 | undefined = mapSourceTravelTimeV2.layerMapData![
                        featureID
                    ] as VectorLayerStateTravelTimeV2 | undefined;
                    if (!travelTimeCurrentDataV2) {
                        return EMPTY;
                    }

                    const dataSetTravelTimeV2Actual = createPlotData('Actueel', chartColors[0]);
                    const dataSetTravelTimeV2FreeFlow = createPlotData('Freeflow', chartColors[1]);

                    const dataSetSpeedActual = createPlotData('Actueel', chartColors[0]);
                    const dataSetFreeflowSpeed = createPlotData('Freeflow', chartColors[1]);

                    for (const dataKey in data) {
                        if (!data.hasOwnProperty(dataKey)) {
                            continue;
                        }

                        const value = data[dataKey];

                        (dataSetTravelTimeV2Actual.y as number[]).push(value.travelTime);
                        (dataSetTravelTimeV2Actual.x as string[]).push(dataKey);
                        (dataSetSpeedActual.y as number[]).push(value.speed);
                        (dataSetSpeedActual.x as string[]).push(dataKey);
                        (dataSetTravelTimeV2FreeFlow.y as number[]).push(travelTimeCurrentDataV2.freeflowTravelTime);
                        (dataSetTravelTimeV2FreeFlow.x as string[]).push(dataKey);
                        (dataSetFreeflowSpeed.y as number[]).push(travelTimeCurrentDataV2.freeflowSpeed);
                        (dataSetFreeflowSpeed.x as string[]).push(dataKey);
                    }

                    sortPlotData(dataSetTravelTimeV2Actual);
                    sortPlotData(dataSetTravelTimeV2FreeFlow);
                    sortPlotData(dataSetSpeedActual);
                    sortPlotData(dataSetFreeflowSpeed);

                    const graphDataTravelTimeV2: ActiveDialogVectorLayerFeatureTravelTimeV2GraphData = {
                        travelTime: [dataSetTravelTimeV2Actual, dataSetTravelTimeV2FreeFlow],
                        speed: [dataSetSpeedActual, dataSetFreeflowSpeed],
                        timeStamp: Date.now(),
                    };

                    return storeNewGraphData(mapSourceID, featureID, graphDataTravelTimeV2);
                case 'avg':
                    const flowDataSets: { [key: string]: Partial<PlotData> } = {};
                    const speedDataSets: { [key: string]: Partial<PlotData> } = {};
                    let indexCounter: number = 0;

                    for (const dataDate in data) {
                        if (!data.hasOwnProperty(dataDate)) {
                            continue;
                        }

                        const dateData = data[dataDate];

                        for (const laneName in dateData) {
                            if (!dateData.hasOwnProperty(laneName)) {
                                continue;
                            }

                            const laneData = dateData[laneName];
                            for (const laneCategoryName in laneData) {
                                if (!laneData.hasOwnProperty(laneCategoryName)) {
                                    continue;
                                }
                                const lookupName = `${laneName}-${laneCategoryName}`;

                                if (!flowDataSets.hasOwnProperty(lookupName)) {
                                    flowDataSets[lookupName] = createPlotData(`${laneCategoryName} - ${laneName}`, chartColors[indexCounter]);
                                }
                                if (!speedDataSets.hasOwnProperty(lookupName)) {
                                    speedDataSets[lookupName] = createPlotData(`${laneCategoryName} - ${laneName}`, chartColors[indexCounter]);
                                }
                                indexCounter++;

                                (flowDataSets[lookupName].x as string[]).push(dataDate);
                                (flowDataSets[lookupName].y as number[]).push(laneData[laneCategoryName].flow);
                                (speedDataSets[lookupName].x as string[]).push(dataDate);
                                (speedDataSets[lookupName].y as number[]).push(laneData[laneCategoryName].speed);
                            }

                        }
                    }

                    sortDataSet(speedDataSets);
                    sortDataSet(flowDataSets);

                    return storeNewGraphData(mapSourceID, featureID, {
                        speed: Object.values(speedDataSets),
                        flow: Object.values(flowDataSets),
                        timeStamp: Date.now(),
                    });
                default: return EMPTY;
            }
        }),
    );

const createPlotData = (name: string, color: string): Partial<PlotData> => {
    return {
        x: [],
        y: [],
        name: name,
        mode: 'lines+markers',
        marker: {
            color: color,
        },
        line: {
            color: color,
        },
    };
};

const sortDataSet = (dataSet: { [key: string]: Partial<PlotData> }) => {
    Object.keys(dataSet).forEach(key => sortPlotData(dataSet[key]));
};

const sortPlotData = (plotData: Partial<PlotData>) => {
    const dataSetsSortedOnDate = sortPairedArrays((plotData.x as string[]), (plotData.y as number[]));
    plotData.x = dataSetsSortedOnDate.x;
    plotData.y = dataSetsSortedOnDate.y;
};

const sortPairedArrays = (x: string[], y: number[]): { x: string[], y: number[] } => {
    if (x.length !== y.length) {
        throw new Error('Arrays must be the same length');
    }

    const paired = x.map((val, idx) => ({x: val, y: y[idx]}));
    paired.sort((a, b) => a.x.localeCompare(b.x)); // Sort based on x

    const sortedX = paired.map(p => p.x);
    const sortedY = paired.map(p => p.y);

    return {x: sortedX, y: sortedY};
};

const graphEpicEffects: Epic = combineEpics(
    loadBridgeDataOnOpenBridgeGraphDialogEffect,
    loadHistoricalDataOnOpenGraphDialogEffect,
    saveBridgeGraphDataOnReceivedBridgeHistoryEffect,
    saveGraphDataOnReceivedHistoricalDataEffect,
);

export default graphEpicEffects;
